import { useCallback, useState } from 'react';

/**
 * Custom hook to manage the validity state of multiple inputs.
 *
 * @param {string[]} initialKeys - An array of keys representing the inputs to track validity for.
 * @returns {Object} - An object containing:
 *   - `validity`: A state object where each key corresponds to an input and its validity status (true/false).
 *   - `handleValidChange`: A callback function to update the validity of a specific input.
 *   - `allValid`: A boolean indicating whether all inputs are valid.
 *
 * @example
 * // Usage example:
 * const { validity, handleValidChange, allValid } = useForm(['email', 'password']);
 *
 * <TextInput
 *   name="email"
 *   pattern="^[^@]+@[^@]+\.[^@]+$"
 *   onValidChange={handleValidChange('email')}
 *   errMessage="Invalid email format"
 * />
 * <TextInput
 *   name="password"
 *   pattern="^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$"
 *   onValidChange={handleValidChange('password')}
 *   errMessage="Username must be at least 3 characters long and can only contain letters, numbers, and underscores"
 * />
 * <button disabled={!allValid}>Submit</button>
 */
const useForm = (
  initialKeys: string[]
): {
  validity: { [p: string]: boolean };
  handleValidChange: (key: string) => (isValid: boolean) => void;
  allValid: boolean;
} => {
  // State to track the validity of each input, initialized to false for each key by default.
  const [validity, setValidity] = useState<{ [key: string]: boolean }>(
    Object.fromEntries(initialKeys.map(key => [key, false]))
  );

  /**
   * Callback to update the validity of a specific input.
   *
   * @param {string} key - The key representing the input to update.
   * @returns {Function} - A function that accepts a boolean indicating the validity of the input.
   */
  const handleValidChange = useCallback(
    (key: string) => (isValid: boolean) => {
      setValidity(prev => ({ ...prev, [key]: isValid }));
    },
    []
  );

  /**
   * Boolean indicating whether all inputs are valid.
   */
  const allValid = Object.values(validity).every(Boolean);

  return { validity, handleValidChange, allValid };
};

export default useForm;
